import type { Geometry } from 'geojson';

import {
  PARAMETER_DATA_VIEW_IDS,
  PARAMETER_DPI,
  PARAMETER_GEOMETRY,
  PARAMETER_MAP_EXTENT,
  PARAMETER_MAP_RESOLUTION,
  PARAMETER_MAP_SCALE,
  PARAMETER_MAP_SIZE,
  PARAMETER_SRS_CODE,
} from '@disy/lib.backend/api-constants';

import { ajax } from 'cadenza/api-client/api';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { HitPolygon } from 'cadenza/api-client/dto/hit-polygon-dto';
import type { MapTipDto } from 'cadenza/api-client/dto/map-tip-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import { PARAMETER_LAYERS } from 'cadenza/api-client/published-api-constants';
import { createWorkbookUrl } from 'cadenza/api-client/workbook-api';
import type { WorkbookLayerCapabilitiesDto } from 'cadenza/api-client/workbook-layer/workbook-layer-capabilities-dto';
import type { ExternalLayerObjectInfoDto } from 'cadenza/map/dto/external-layer-object-info-dto';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { MapHitResultDto } from 'cadenza/workbook/selection/model/map-hit-result-dto';

export function getWorkbookMapViewContextUrl(workbookNameAndHash: WorkbookNameAndHash, viewId: ViewId): string {
  return `${createWorkbookUrl(workbookNameAndHash, { viewId })}/mapConfiguration`;
}

interface GetHitOptions {
  workbookNameAndHash: WorkbookNameAndHash;
  viewId: ViewId;
  srsCode: string;
  mapSize: DimensionDto;
  mapScale: number;
  dpi: number;
  geometry: Geometry;
}

export interface GetHitObjectOptions extends GetHitOptions {
  mapExtent: WorldBoxDto;
  adjustCurrentSelection: boolean;
  selectableLayers?: LayerId[];
}

export interface GetExternalLayerObjectInfo {
  workbookNameAndHash: WorkbookNameAndHash;
  viewId: ViewId;
  layerId: LayerId;
  srsCode: string;
  mapSize: DimensionDto;
  dpi: number;
  geometry: Geometry;
  mapExtent: WorldBoxDto;
}

interface GetHitFromOtherLayers {
  workbookNameAndHash: WorkbookNameAndHash;
  viewId: ViewId;
  geometry: Geometry;
  srsCode: string;
  mapSize: DimensionDto;
  mapScale: number;
  dpi: number;
  mapExtent: WorldBoxDto;
  selectableLayers?: LayerId[];
}

export function getHitObjects(
  {
    workbookNameAndHash,
    viewId,
    srsCode,
    mapExtent,
    mapSize,
    dpi,
    mapScale,
    geometry,
    adjustCurrentSelection,
    selectableLayers,
  }: GetHitObjectOptions,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_GEOMETRY]: JSON.stringify(geometry),
    [PARAMETER_SRS_CODE]: srsCode,
    [PARAMETER_DPI]: dpi,
    [PARAMETER_MAP_SCALE]: mapScale,
    [PARAMETER_LAYERS]: selectableLayers?.join(),
    adjustCurrentSelection,
  };
  return ajax<MapHitResultDto>(
    `${createWorkbookUrl(workbookNameAndHash, { viewId })}/objects?&${PARAMETER_MAP_SIZE}=${mapSize}&${PARAMETER_MAP_EXTENT}=${mapExtent}`,
    { signal, data },
  );
}

export function getExternalLayerObjectInfo(
  { workbookNameAndHash, viewId, layerId, srsCode, mapExtent, mapSize, dpi, geometry }: GetExternalLayerObjectInfo,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_GEOMETRY]: JSON.stringify(geometry),
    [PARAMETER_SRS_CODE]: srsCode,
    [PARAMETER_DPI]: dpi,
  };
  return ajax<ExternalLayerObjectInfoDto>(
    `${createWorkbookUrl(workbookNameAndHash, { viewId })}/layers/${layerId}/objects?&${PARAMETER_MAP_SIZE}=${mapSize}&${PARAMETER_MAP_EXTENT}=${mapExtent}`,
    { signal, data },
  );
}

export function getOtherHitObjects(
  {
    workbookNameAndHash,
    viewId,
    srsCode,
    mapExtent,
    mapSize,
    dpi,
    mapScale,
    geometry,
    selectableLayers,
  }: GetHitFromOtherLayers,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_GEOMETRY]: JSON.stringify(geometry),
    [PARAMETER_SRS_CODE]: srsCode,
    [PARAMETER_DPI]: dpi,
    [PARAMETER_MAP_SCALE]: mapScale,
    [PARAMETER_LAYERS]: selectableLayers?.join(),
  };
  return ajax<MapHitResultDto[]>(
    `${createWorkbookUrl(workbookNameAndHash, { viewId })}/other-objects?&${PARAMETER_MAP_SIZE}=${mapSize}&${PARAMETER_MAP_EXTENT}=${mapExtent}`,
    { signal, data },
  );
}

interface GetHitGeometriesOptions extends GetHitOptions {
  /** Geometry types that should be included in the response. Note that the "multi" variant of the base type will also be included, e.g. "POINT" will also include "MULTIPOINT" */
  acceptedGeometryTypes?: ('POINT' | 'LINE' | 'POLYGON' | 'RASTER')[]; // see Java enum BaseGeometryType
}

export function getHitGeometries(
  { workbookNameAndHash, viewId, srsCode, mapSize, dpi, geometry, acceptedGeometryTypes }: GetHitGeometriesOptions,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_MAP_SIZE]: String(mapSize),
    userSelectionGeometry: JSON.stringify(geometry),
    [PARAMETER_SRS_CODE]: srsCode,
    [PARAMETER_DPI]: dpi,
    acceptedGeometryTypes,
  };
  return ajax<HitPolygon[]>(
    createWorkbookUrl(workbookNameAndHash, { viewId }) + '/geometries',
    { data, signal, traditional: true }, // traditional is required to send array query parameters as expected by Spring MVC
  );
}

interface GetMapTipOptions {
  workbookNameAndHash: WorkbookNameAndHash;
  viewId: ViewId;
  dataViewIds: DataViewId[];
  srsCode: string;
  mapExtent: WorldBoxDto;
  mapSize: DimensionDto;
  mapScale: number;
  dpi: number;
  geometry: Geometry;
  adjustCurrentSelection: boolean;
}

export function getMapTip(
  { workbookNameAndHash, viewId, dataViewIds, srsCode, mapExtent, mapSize, mapScale, dpi, geometry }: GetMapTipOptions,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_DATA_VIEW_IDS]: dataViewIds,
    [PARAMETER_GEOMETRY]: JSON.stringify(geometry),
    [PARAMETER_SRS_CODE]: srsCode,
    [PARAMETER_MAP_SCALE]: mapScale,
    [PARAMETER_DPI]: dpi,
  };
  return ajax<MapTipDto>(
    `${createWorkbookUrl(workbookNameAndHash, { viewId })}/mapTip?${PARAMETER_MAP_SIZE}=${mapSize}&${PARAMETER_MAP_EXTENT}=${mapExtent}`,
    { signal, data },
  );
}

export function getLayerExtent(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  layerId: LayerId,
  srsCode: string,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_SRS_CODE]: srsCode,
  };
  return ajax<WorldBoxDto>(`${createWorkbookUrl(workbookNameAndHash, { viewId })}/layers/${layerId}/content-extent`, {
    data,
    signal,
  });
}

export function getExtentForLayers(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  layerIds: LayerId[],
  srsCode: string,
  signal: AbortSignal,
) {
  const params = new URLSearchParams();
  params.set(PARAMETER_SRS_CODE, srsCode);
  params.set('layerIds', layerIds.join());
  return ajax<WorldBoxDto>(`${createWorkbookUrl(workbookNameAndHash, { viewId })}/content-extent?${params}`, {
    signal,
  });
}

export function getDataExtent(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  srsCode: string,
  signal: AbortSignal,
) {
  const data = {
    [PARAMETER_SRS_CODE]: srsCode,
  };
  return ajax<WorldBoxDto>(`${createWorkbookUrl(workbookNameAndHash, { viewId })}/content-extent`, { data, signal });
}

export function getAutozoomExtent(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  mapSrs: string,
  signal: AbortSignal,
) {
  const mapParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs,
  });
  return ajax<WorldBoxDto>(`${createWorkbookUrl(workbookNameAndHash, { viewId })}/autozoom-extent?${mapParams}`, {
    signal,
  });
}

export function getSimplifiedGeometryForSelectedFeature(
  workbookNameAndHash: WorkbookNameAndHash,
  mapSrs: string,
  mapResolution: number,
  signal: AbortSignal,
) {
  const searchParams = new URLSearchParams({
    [PARAMETER_SRS_CODE]: mapSrs,
    [PARAMETER_MAP_RESOLUTION]: String(mapResolution),
  });
  return ajax<Geometry>(`${createWorkbookUrl(workbookNameAndHash)}/selection/simplifiedGeometry?${searchParams}`, {
    signal,
  });
}

export function getWorkbookLayerCapabilities(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: string,
  layerId: string,
) {
  return ajax<WorkbookLayerCapabilitiesDto>(
    `${createWorkbookUrl(workbookNameAndHash)}/views/${viewId}/layers/${layerId}/capabilities`,
  );
}
